import { useCallback, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";

import { PortfolioVsSpyChart } from "@/components/PortfolioVsSpyChart";
import { buildBacktestAccount } from "@/lib/backtest-chart";
import { formatUSD } from "@/lib/formatters";

// ---- Types ----------------------------------------------------------------

interface BacktestChartProps {
  /** Stable id — isolates the chart's per-account internal state. */
  id: string;
  /** Simulated equity curve (daily values, oldest first). */
  curve: number[];
}

interface HeroState {
  pctChange: number;
  usdChange: number;
  dateLabel: string;
  isHover: boolean;
}

// ---- Component ------------------------------------------------------------

/**
 * Backtest equity chart (Sergey 2026-05-21) — renders the *same* full
 * account chart (`PortfolioVsSpyChart`, v2 layout: W/1M/3M/6M/YTD/1Y/All
 * presets, $/% toggle, SPY + Target overlays) for a bot's simulated
 * curve. The v2 chart expects the parent to own the value heading, so
 * this wrapper draws the compact delta hero above it and feeds the hero
 * from the chart's hover/range callbacks.
 *
 * Used by the Backtest → Graph tab in both the My Bots and Marketplace
 * bot drawers.
 */
export function BacktestChart({ id, curve }: BacktestChartProps) {
  const account = useMemo(() => buildBacktestAccount(id, curve), [id, curve]);

  // Seed the hero from the full curve so there's no 0.00% flash before
  // the chart's first onHoverState/onRangeChange callback lands.
  const seed = useMemo<HeroState>(() => {
    const first = curve[0] || 0;
    const last = curve[curve.length - 1] || 0;
    return {
      pctChange: first ? ((last - first) / first) * 100 : 0,
      usdChange: last - first,
      dateLabel: "",
      isHover: false
    };
  }, [curve]);

  const [hero, setHero] = useState<HeroState>(seed);
  const [rangeLabel, setRangeLabel] = useState("All time");

  // STABLE callback identities — critical. PortfolioVsSpyChart fires
  // onHoverState / onRangeChange from effects that list the callbacks
  // in their dependency arrays. A fresh inline arrow each render would
  // re-trigger those effects → re-fire → setState → re-render → an
  // infinite loop that pegs the main thread and freezes the drawer
  // (Sergey 2026-05-21: "impossible to switch tabs"). useCallback keeps
  // the identities fixed so the effects only run on real changes.
  const handleHover = useCallback(
    (
      s:
        | null
        | {
            idx: number;
            usd: number;
            pctChange: number;
            usdChange: number;
            dateLabel: string;
            isHover: boolean;
          }
    ) => {
      setHero(
        s
          ? {
              pctChange: s.pctChange,
              usdChange: s.usdChange,
              dateLabel: s.dateLabel,
              isHover: s.isHover
            }
          : seed
      );
    },
    [seed]
  );
  const handleRange = useCallback(
    (info: { label: string }) => setRangeLabel(info.label),
    []
  );

  const up = hero.pctChange >= 0;

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      {/* Delta hero — the heading the v2 chart leaves to its parent. */}
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography
          sx={(t) => ({
            fontWeight: 800,
            fontSize: 20,
            fontFeatureSettings: '"tnum" 1',
            color: up ? t.palette.success.main : t.palette.error.main
          })}
        >
          {up ? "▲" : "▼"} {up ? "+" : "−"}
          {Math.abs(hero.pctChange).toFixed(2)}%
        </Typography>
        <Typography
          sx={(t) => ({
            fontWeight: 700,
            fontFeatureSettings: '"tnum" 1',
            color: up ? t.palette.success.main : t.palette.error.main
          })}
        >
          {up ? "+" : "−"}
          {formatUSD(Math.abs(hero.usdChange))}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {hero.isHover && hero.dateLabel ? hero.dateLabel : rangeLabel}
        </Typography>
      </Stack>

      <PortfolioVsSpyChart
        account={account}
        v2
        compact
        onHoverState={handleHover}
        onRangeChange={handleRange}
      />

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        Backtest results are simulated and not reflective of actual
        performance.
      </Typography>
    </Box>
  );
}
